import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Injector,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  Validators,
} from '@angular/forms';
import { reduce } from 'lodash';
import { Subject, fromEvent, takeUntil } from 'rxjs';

import { FormFieldControlDirective } from '../form-field/form-field-control.directive';

@Component({
    selector: 'lui-file-drag-and-drop',
    template: `
    <div class="lui-file-drag-and-drop-overlay"></div>
    <ng-content></ng-content>
    <input
      class="lui-file-drag-and-drop-input"
      type="file"
      [multiple]="multiple"
      [attr.accept]="acceptAttr"
      #input
    />
  `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FileDragAndDropComponent,
        },
        {
            provide: FormFieldControlDirective,
            useExisting: FileDragAndDropComponent,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FileDragAndDropComponent
  implements
    OnInit,
    AfterViewInit,
    DoCheck,
    ControlValueAccessor,
    FormFieldControlDirective<any>
{
  @Output() filesChanged = new EventEmitter<File[]>();
  @Output() filesAdded = new EventEmitter<File[]>();
  @Output() filesRemoved = new EventEmitter<File[]>();
  @Output() filesRejected = new EventEmitter<File[]>();
  @Output() filesDropped = new EventEmitter<File[]>();

  @Input() button!: HTMLElement;

  @Input() dropOnly = false;

  @Input() get multiple() {
    return this._multiple;
  }
  set multiple(value: BooleanInput) {
    const boolValue = coerceBooleanProperty(value);
    this._multiple = boolValue;
    this._filesCollection = new SelectionModel(boolValue);
    this._filesCollection.compareWith = (f1, f2) => f1.name === f2.name;
  }
  private _multiple = true;

  @Input() accept!: string[];

  @Input() filesizeLimit: number | null | undefined = null;

  @Input() disabled = false;

  @Input() emitOnly = false;

  get acceptAttr() {
    return this.accept.join(',');
  }

  @ViewChild('input') inputEl!: ElementRef<HTMLInputElement>;

  @HostBinding('class') hostClass = 'lui-file-drag-and-drop';

  @HostBinding('class.lui-file-drag-and-drop-dragover') isDragover = false;

  @Input()
  get required(): boolean {
    return (
      this._required ??
      this.ngControl?.control?.hasValidator(Validators.required) ??
      false
    );
  }
  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
  }
  protected _required: boolean | undefined;

  _destroy = new Subject<void>();

  _filesCollection: SelectionModel<File> = new SelectionModel(this._multiple);

  @Input() get value() {
    return this._value;
  }
  set value(files: File | File[]) {
    const filesArr = Array.isArray(files) ? files : [files];

    this._filesCollection.setSelection(...filesArr);
  }
  _value: File | File[] = [];

  ngControl!: NgControl | null;

  readonly stateChanges: Subject<void> = new Subject<void>();
  readonly empty!: boolean;
  readonly errorState!: boolean;
  readonly focused!: boolean;
  touched = false;

  _injector = inject(Injector);

  _onChange: (value: unknown | unknown[]) => void = () => undefined;

  _onTouched: () => void = () => undefined;

  @HostListener('dragover', ['$event']) onDragOver(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();

    if (this.disabled) return;

    if (!this.isDragover) {
      this.isDragover = true;
    }
  }

  @HostListener('dragleave', ['$event']) onDragLeave(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();

    if (this.disabled) return;

    this.isDragover = false;
  }

  @HostListener('drop', ['$event']) onDrop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (this.disabled) return;

    const dataTransfer = e.dataTransfer;
    this.isDragover = false;

    if (!dataTransfer) {
      return;
    }

    const readFiles = reduce(
      Array.from(e.dataTransfer.items),
      (acc, item) => {
        const y = item.webkitGetAsEntry();

        if (y?.isFile) {
          const file = item.getAsFile();
          if (file) {
            acc.push(file);
          }
        }
        // Future improvement: handle directories
        // see: https://stackoverflow.com/questions/3590058/does-html5-allow-drag-drop-upload-of-folders-or-a-folder-tree
        return acc;
      },
      [] as File[]
    );

    const files = this.multiple ? readFiles : [readFiles[0]];

    if (files.length > 0) {
      const validatedFiles = this._validateFiles(files);
      if (this.dropOnly) {
        this.filesDropped.emit(validatedFiles.accepted);
        return;
      }

      if (validatedFiles.accepted.length) {
        this.addFiles(validatedFiles.accepted);
      }

      if (validatedFiles.rejected.length) {
        this.filesRejected.emit(validatedFiles.rejected);
      }
    }
  }

  ngOnInit(): void {
    this.ngControl = this._injector.get(NgControl, undefined, {
      optional: true,
    });

    this._filesCollection.compareWith = (f1, f2) => f1.name === f2.name;
  }

  ngDoCheck(): void {
    if (this.ngControl && this.ngControl.touched !== this.touched) {
      this.stateChanges.next();
    }
  }

  ngAfterViewInit(): void {
    if (this.button && this.inputEl) {
      fromEvent(this.button, 'click')
        .pipe(takeUntil(this._destroy))
        .subscribe(() => {
          if (this.disabled) return;
          this.inputEl.nativeElement.click();
        });
    }

    if (this.inputEl) {
      fromEvent(this.inputEl.nativeElement, 'change')
        .pipe(takeUntil(this._destroy))
        .subscribe((e: Event) => {
          const target = e.target as HTMLInputElement;

          if (target.files && target.files.length > 0) {
            const validatedFiles = this._validateFiles(
              Array.from(target.files)
            );

            if (validatedFiles.accepted.length) {
              this.addFiles(validatedFiles.accepted);
            }

            if (validatedFiles.rejected.length) {
              this.filesRejected.emit(validatedFiles.rejected);
            }
            this.inputEl.nativeElement.value = '';
          }
        });
    }

    this.ngControl &&
      this.ngControl.statusChanges?.subscribe((v) => {
        this.stateChanges.next();
      });

    this._filesCollection.changed
      .pipe(takeUntil(this._destroy))
      .subscribe((changes) => {
        const files = this._filesCollection.selected;
        this._setFiles(files);

        this.filesAdded.emit(changes.added);
        this.filesRemoved.emit(changes.removed);
      });
  }

  markAsTouched() {
    if (!this.touched) {
      this._onTouched();
      this.touched = true;
    }
  }

  private _validateFiles(files: File[]): {
    accepted: File[];
    rejected: File[];
  } {
    if (!this.accept || this.accept.length === 0) {
      return { accepted: files, rejected: [] };
    }
    return files.reduce(
      (acc, file) => {
        const accepted = this.accept.some((acceptedType) => {
          // Split the accepted type by '/'
          const [type, subtype] = acceptedType.split('/');
          // check if the subtype is a wildcard
          const wildcard = subtype === '*';
          // Compare the file's type with the wildcard's subtype or the acceptedType
          return wildcard
            ? file.type.startsWith(`${type}/`)
            : acceptedType === file.type;
        });

        const sizeAccepted =
          !this.filesizeLimit || file.size <= this.filesizeLimit;

        if (accepted && sizeAccepted) {
          acc.accepted.push(file);
        } else {
          acc.rejected.push(file);
        }
        return acc;
      },
      { accepted: [] as File[], rejected: [] as File[] }
    );
  }

  removeFile(file: File) {
    this._filesCollection.deselect(file);
  }

  writeValue(files: unknown) {
    const verifiedFiles = this._verifyFiles(files);

    if (verifiedFiles) {
      const validatedFiles = this._validateFiles(verifiedFiles);
      this._filesCollection.setSelection(...validatedFiles.accepted);
    } else {
      this._filesCollection.clear();
    }
    this.stateChanges.next();
  }

  registerOnChange(fn: (value: unknown) => void) {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this._onTouched = fn;
  }

  private addFiles(files: File[]) {
    if (!this.emitOnly) {
      this._filesCollection.select(...files);
    } else {
      this.filesAdded.emit(files);
    }
  }

  private _setFiles(files: File[]) {
    if (this.multiple) {
      this._value = Array.isArray(files) ? files : [files];
    } else {
      this._value = Array.isArray(files) ? files[0] : files;
    }
    this.filesChanged.emit(files);
    this._onChange(this.value);
    this.markAsTouched();
    this.stateChanges.next();
  }

  private _verifyFiles(files: unknown) {
    let verifiedFiles: File | File[] | null;

    verifiedFiles = this._isFile(files) ? Array.from([files]) : null;
    verifiedFiles = this._isFilesOrFileList(files) ? Array.from(files) : null;
    return verifiedFiles;
  }

  private _isFile(file: unknown): file is File {
    return file instanceof File;
  }

  private _isFilesOrFileList(files: unknown): files is File[] | FileList {
    if (!Array.isArray(files)) {
      return false;
    }
    return files.some((file) => this._isFile(file));
  }
}
